// src/components/AuthModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../../../firebase/firebase";
import {
  GoogleAuthProvider,
  GithubAuthProvider,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
} from "firebase/auth";

// Provider icons (keep your existing import path if that's where these live)
import { GoogleIcon, GitHubIcon, LogoIcon } from "../../primatives/Icons";

export default function AuthModal({ open, onClose, brandName = "DegreeClassifyUK" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ type: "idle", message: "" }); // idle | ok | err | loading

  const actionCodeSettings = useMemo(
    () => ({
      url: window.location.origin,
      handleCodeInApp: true,
    }),
    []
  );

  // If user comes back via email link, complete sign-in
  useEffect(() => {
    if (!open) return;

    const href = window.location.href;
    if (!isSignInWithEmailLink(auth, href)) return;

    (async () => {
      try {
        setStatus({ type: "loading", message: "Completing sign-in…" });

        let storedEmail = window.localStorage.getItem("emailForSignIn");
        if (!storedEmail) {
          storedEmail = window.prompt("Confirm your email to complete sign-in") || "";
        }
        if (!storedEmail) {
          setStatus({ type: "err", message: "Email is required to complete sign-in." });
          return;
        }

        await signInWithEmailLink(auth, storedEmail, href);
        window.localStorage.removeItem("emailForSignIn");

        setStatus({ type: "ok", message: "Signed in successfully." });
        window.history.replaceState({}, document.title, window.location.origin);
        onClose?.();
      } catch (e) {
        setStatus({ type: "err", message: e?.message || "Failed to complete sign-in." });
      }
    })();
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setStatus({ type: "idle", message: "" });
    }
  }, [open]);

  const onSendEmailLink = async (e) => {
    e?.preventDefault?.();
    const cleaned = (email || "").trim().toLowerCase();

    if (!cleaned || !/^\S+@\S+\.\S+$/.test(cleaned)) {
      setStatus({ type: "err", message: "Enter a valid email address." });
      return;
    }

    try {
      setStatus({ type: "loading", message: "Sending sign-in link…" });
      await sendSignInLinkToEmail(auth, cleaned, actionCodeSettings);
      window.localStorage.setItem("emailForSignIn", cleaned);
      setStatus({
        type: "ok",
        message: "Check your inbox. Open the sign-in link to continue.",
      });
    } catch (e2) {
      setStatus({ type: "err", message: e2?.message || "Failed to send sign-in link." });
    }
  };

  const onGoogle = async () => {
    try {
      setStatus({ type: "loading", message: "Opening Google sign-in…" });
      await signInWithPopup(auth, new GoogleAuthProvider());
      setStatus({ type: "ok", message: "Signed in successfully." });
      onClose?.();
    } catch (e) {
      setStatus({ type: "err", message: e?.message || "Google sign-in failed." });
    }
  };

  const onGitHub = async () => {
    try {
      setStatus({ type: "loading", message: "Opening GitHub sign-in…" });
      await signInWithPopup(auth, new GithubAuthProvider());
      setStatus({ type: "ok", message: "Signed in successfully." });
      onClose?.();
    } catch (e) {
      setStatus({ type: "err", message: e?.message || "GitHub sign-in failed." });
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-label="Close sign-in"
      />

      {/* Card */}
      <div className="relative w-full max-w-md rounded-3xl border border-neutral-200 bg-white p-6 shadow-2xl dark:border-neutral-800 dark:bg-neutral-950">
        {/* Close button (top-right) */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="
            absolute right-3 top-3
            inline-flex h-8 w-8 items-center justify-center
            rounded-lg text-neutral-500
            hover:bg-neutral-100 hover:text-neutral-800
            dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-100
          "
        >
          <span className="text-lg leading-none">&times;</span>
        </button>

        {/* Header stack: login icon -> title -> description (all centered) */}
        <div className="flex flex-col items-center text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
            <LogoIcon className="h-128 w-128" />
          </div>

          <div className="mt-2 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
            Sign In with Email
          </div>

          <div className="mt-6 text-sm text-neutral-600 dark:text-neutral-400">
            Sign in with a passwordless email link, or continue with Github or Google.
          </div>
        </div>

        {/* Status */}
        {status.type !== "idle" ? (
          <div
            className={[
              "mt-6 rounded-2xl border p-3 text-sm",
              status.type === "ok"
                ? "border-green-200 bg-green-50 text-green-800 dark:border-green-900/40 dark:bg-green-950/40 dark:text-green-200"
                : status.type === "err"
                ? "border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200"
                : "border-neutral-200 bg-white text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200",
            ].join(" ")}
          >
            {status.message}
          </div>
        ) : null}

        {/* Email form */}
        <form className="mt-6 space-y-3" onSubmit={onSendEmailLink}>
          <label className="block text-sm font-medium text-neutral-800 dark:text-neutral-200">
            Email address
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@uni.ac.uk"
              className="mt-2 w-full rounded-2xl border border-neutral-200 bg-white px-3 py-3 text-sm text-neutral-900 outline-none focus:ring-2 focus:ring-neutral-300 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:ring-neutral-700"
              autoComplete="email"
            />
          </label>

          <button
            type="submit"
            disabled={status.type === "loading"}
            className="w-full rounded-2xl border border-neutral-200 bg-neutral-900 px-4 py-3 text-sm font-semibold text-white hover:bg-neutral-800 disabled:opacity-60 dark:border-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200"
          >
            Continue with email link
          </button>

          <div className="text-xs text-neutral-500 dark:text-neutral-500">
            We will email you a one-time sign-in link. No password required.
          </div>
        </form>

        {/* Divider */}
        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
          <div className="text-xs text-neutral-500">or</div>
          <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
        </div>

        {/* Provider icons */}
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onGitHub}
            disabled={status.type === "loading"}
            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-neutral-200 bg-white text-neutral-900 shadow-sm hover:bg-neutral-100 disabled:opacity-60 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:hover:bg-neutral-900"
            aria-label="Continue with GitHub"
            title="Continue with GitHub"
          >
            <GitHubIcon className="h-6 w-6" />
          </button>

          <button
            type="button"
            onClick={onGoogle}
            disabled={status.type === "loading"}
            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-neutral-200 bg-white text-neutral-900 shadow-sm hover:bg-neutral-100 disabled:opacity-60 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:hover:bg-neutral-900"
            aria-label="Continue with Google"
            title="Continue with Google"
          >
            <GoogleIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mt-4 text-center text-xs text-neutral-500 dark:text-neutral-500">
          By continuing, you agree to the app’s privacy policy.
        </div>
      </div>
    </div>
  );
}
